// src/pages/payment/PaymentTransactionDetailsPage.js - Organized layout
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import { getPaymentTransaction } from '../../api/paymentTransactionAPI';
import { getSession } from '../../api/sessionAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import SessionPaymentStatus from '../../components/session/SessionPaymentStatus';

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString('en-GB');
  } catch {
    return dateString;
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'succeeded':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    case 'canceled':
    case 'cancelled':
      return 'default';
    case 'refunded':
      return 'info';
    default:
      return 'default';
  }
};

const PaymentTransactionDetailsPage = () => {
  const { transactionId } = useParams();

  // Fetch payment transaction details
  const { 
    data: transaction, 
    isLoading: isLoadingTransaction, 
    isError: isTransactionError, 
    error: transactionError 
  } = useQuery({
    queryKey: ['payment-transaction', transactionId],
    queryFn: () => getPaymentTransaction(parseInt(transactionId)),
    enabled: !!transactionId
  });

  // Fetch associated session details if session ID exists
  const { 
    data: session, 
    isLoading: isLoadingSession 
  } = useQuery({
    queryKey: ['session', transaction?.PaymentTransactionSessionId],
    queryFn: () => getSession(transaction.PaymentTransactionSessionId),
    enabled: !!transaction?.PaymentTransactionSessionId
  });

  if (isLoadingTransaction) {
    return <LoadingSpinner />;
  }

  if (isTransactionError) {
    return <ErrorAlert message={transactionError instanceof Error ? transactionError.message : 'Error loading payment transaction'} />;
  }

  if (!transaction) {
    return <ErrorAlert message="Payment transaction not found" />;
  }

  const shouldShowPaymentButton = () => {
    const paymentStatus = transaction.PaymentTransactionPaymentStatus?.toLowerCase();
    const amount = transaction.PaymentTransactionAmount || 0;
    return paymentStatus && ['pending', 'failed'].includes(paymentStatus) && session && amount > 0;
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Payment Transaction #{transaction.PaymentTransactionId}
        </Typography>
        <Button
          component={Link}
          to="/payment"
          startIcon={<ArrowBackIcon />}
        >
          Back to Payments
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Payment Transaction Details */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Transaction Details</Typography>
              {transaction.PaymentTransactionSessionId && (
                <Button
                  component={Link}
                  to={`/sessions/${transaction.PaymentTransactionSessionId}`}
                  variant="outlined"
                  size="small"
                >
                  View Session #{transaction.PaymentTransactionSessionId}
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* First Row */}
            <Grid container spacing={3} mb={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Transaction ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    #{transaction.PaymentTransactionId}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Amount
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    ${transaction.PaymentTransactionAmount?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Payment Status
                  </Typography>
                  <Chip 
                    label={transaction.PaymentTransactionPaymentStatus || 'Unknown'} 
                    color={getStatusColor(transaction.PaymentTransactionPaymentStatus)}
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Company ID
                  </Typography>
                  <Typography variant="body1">
                    {transaction.PaymentTransactionCompanyId}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Second Row */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Site ID
                  </Typography>
                  <Typography variant="body1">
                    {transaction.PaymentTransactionSiteId}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Charger ID
                  </Typography>
                  <Typography variant="body1">
                    {transaction.PaymentTransactionChargerId}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Driver ID
                  </Typography>
                  <Typography variant="body1">
                    {transaction.PaymentTransactionDriverId || '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Transaction Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(transaction.PaymentTransactionDateTime)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Stripe Intent ID (if available) */}
            {transaction.PaymentTransactionStripeIntentId && (
              <Box mt={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Stripe Payment Intent ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {transaction.PaymentTransactionStripeIntentId}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Session Details and Payment Form - Full Width Below Transaction Details */}
        <Grid item xs={12}>
          {isLoadingSession ? (
            <LoadingSpinner />
          ) : session ? (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Associated Session Details
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {/* Single Row with Four Columns - Full Width */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Session ID
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      #{session.ChargeSessionId}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Session Cost
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      ${session.ChargerSessionCost?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Energy Used
                    </Typography>
                    <Typography variant="body1">
                      {session.ChargerSessionEnergyKWH?.toFixed(2) || '0.00'} kWh
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Session Status
                    </Typography>
                    <Chip 
                      label={session.ChargerSessionStatus || 'Unknown'} 
                      color={getStatusColor(session.ChargerSessionStatus)}
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Payment Status and Button */}
              {shouldShowPaymentButton() ? (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Payment required for this session
                  </Alert>
                  <Button
                    component={Link}
                    to={`/sessions/${session.ChargeSessionId}/payment`}
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<PaymentIcon />}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    Pay ${session.ChargerSessionCost?.toFixed(2) || '0.00'}
                  </Button>
                </Box>
              ) : (
                <SessionPaymentStatus session={session} />
              )}
            </Paper>
          ) : transaction.PaymentTransactionSessionId ? (
            <Alert severity="error">
              Session #{transaction.PaymentTransactionSessionId} not found
            </Alert>
          ) : (
            <Alert severity="info">
              No associated session for this transaction
            </Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentTransactionDetailsPage;